import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";

const SLIDES = ["shoes", "shirts", "trousers"];
const LABELS = { shoes: "Shoes", shirts: "Shirts", trousers: "Trousers" };

export default function HeroCarousel() {
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const [paused, setPaused] = useState(false);

  // Autoplay until first user interaction
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      setDir(1);
      setIdx((i) => (i + 1) % SLIDES.length);
    }, 6000);
    return () => clearInterval(t);
  }, [paused]);

  const go = (delta) => {
    setPaused(true);
    setDir(delta);
    setIdx((i) => (i + delta + SLIDES.length) % SLIDES.length);
  };

  const jumpTo = (i) => {
    setPaused(true);
    setDir(i > idx ? 1 : -1);
    setIdx(i);
  };

  const variants = {
    enter: (d) => ({ opacity: 0, x: d * 30 }),
    center: { opacity: 1, x: 0 },
    exit: (d) => ({ opacity: 0, x: -d * 30 }),
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      data-testid="hero-carousel"
      className="relative w-full max-w-[460px] ml-auto"
    >
      {/* Card stage with fixed aspect */}
      <div className="relative aspect-[4/5] w-full">
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={SLIDES[idx]}
            custom={dir}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0"
          >
            {SLIDES[idx] === "shoes" && <FeetCard />}
            {SLIDES[idx] === "shirts" && <ShirtsCard />}
            {SLIDES[idx] === "trousers" && <TrousersCard />}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Pagination row OUTSIDE the card */}
      <div className="mt-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {SLIDES.map((s, i) => (
            <button
              key={s}
              data-testid={`hero-carousel-dot-${s}`}
              aria-label={`Go to ${s}`}
              onClick={() => jumpTo(i)}
              className="group flex items-center gap-2"
            >
              <span
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-6 bg-[#0A0A0A]" : "w-1.5 bg-black/20 group-hover:bg-black/40"
                }`}
              />
              {i === idx && (
                <span className="text-[11px] uppercase tracking-[0.22em] text-neutral-600">
                  {LABELS[s]}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1.5">
          <button
            data-testid="hero-carousel-prev"
            onClick={() => go(-1)}
            aria-label="Previous"
            className="h-8 w-8 grid place-items-center rounded-full bg-white border border-black/10 hover:bg-neutral-50 transition-all active:scale-95"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <button
            data-testid="hero-carousel-next"
            onClick={() => go(1)}
            aria-label="Next"
            className="h-8 w-8 grid place-items-center rounded-full bg-white border border-black/10 hover:bg-neutral-50 transition-all active:scale-95"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

/* ---------- Cards ---------- */

function CardShell({ kind, children, footer }) {
  return (
    <div className="absolute inset-0 rounded-[28px] glass-card overflow-hidden">
      <div
        className="absolute -top-24 -left-16 h-72 w-72 rounded-full drift"
        style={{
          background:
            "radial-gradient(circle at 30% 30%, rgba(0,0,0,0.25), rgba(0,0,0,0.02) 60%, transparent 70%)",
          filter: "blur(30px)",
        }}
      />
      <div
        className="absolute bottom-0 right-0 h-80 w-80 rounded-full drift"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, rgba(0,0,0,0.12), transparent 65%)",
          filter: "blur(28px)",
          animationDelay: "-4s",
        }}
      />
      <div className="relative h-full flex flex-col justify-between p-7" data-testid={`hero-card-${kind}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-black/50" />
            <span className="h-2 w-2 rounded-full bg-black/20" />
            <span className="h-2 w-2 rounded-full bg-black/10" />
          </div>
          <span className="text-[10px] tracking-[0.22em] uppercase text-neutral-500">
            FitID · {LABELS[kind]}
          </span>
        </div>

        <div className="flex-1 relative grid place-items-center">{children}</div>

        {footer}
      </div>
    </div>
  );
}

function FeetCard() {
  const location = useLocation();
  const isCompanies = location.pathname.startsWith("/companies");
  const scrollTarget = isCompanies ? "#contact" : "#sizing-demo";
  const ctaLabel = isCompanies ? "Book a pilot" : "Create FitID";

  const onClick = () => {
    const el = document.querySelector(scrollTarget);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <CardShell
      kind="shoes"
      footer={
        <div className="flex items-center justify-between gap-3">
          <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 leading-snug max-w-[58%]">
            Length · Width
          </div>
          <button
            data-testid="hero-shoes-cta"
            onClick={onClick}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#0A0A0A] text-white text-[12px] px-3.5 py-2 hover:bg-neutral-800 transition-all active:scale-[0.98]"
          >
            {ctaLabel}
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      }
    >
      <FootVisual />
    </CardShell>
  );
}

function ShirtsCard() {
  return (
    <CardShell
      kind="shirts"
      footer={
        <div className="flex items-center justify-between gap-3">
          <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 leading-snug max-w-[58%]">
            Sleeve · Shoulder · Length · Chest
          </div>
          <button
            data-testid="hero-shirts-cta"
            disabled
            className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white/80 text-[#0A0A0A] text-[12px] px-3.5 py-2 cursor-not-allowed"
          >
            <Clock className="h-3 w-3" />
            Coming soon
          </button>
        </div>
      }
    >
      <ShirtVisual />
    </CardShell>
  );
}

function TrousersCard() {
  return (
    <CardShell
      kind="trousers"
      footer={
        <div className="flex items-center justify-between gap-3">
          <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 leading-snug max-w-[58%]">
            Inseam · Waist · Leg opening
          </div>
          <button
            data-testid="hero-trousers-cta"
            disabled
            className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white/80 text-[#0A0A0A] text-[12px] px-3.5 py-2 cursor-not-allowed"
          >
            <Clock className="h-3 w-3" />
            Coming soon
          </button>
        </div>
      }
    >
      <TrousersVisual />
    </CardShell>
  );
}

/* ---------- Visuals ---------- */

function FootVisual() {
  return (
    <div className="relative w-44 h-56 flex items-center justify-center">
      <img
        src="/foot.png"
        alt="Foot"
        className="relative h-full w-auto object-contain"
        style={{ filter: "invert(1)", mixBlendMode: "multiply" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute top-0 bottom-0 left-2 border-l border-dashed border-black/20"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute left-2 right-0 top-[28%] border-t border-dashed border-black/30"
      />
    </div>
  );
}

function ShirtVisual() {
  return (
    <div className="relative w-52 h-56">
      <svg
        viewBox="0 0 220 240"
        className="absolute inset-0 w-full h-full text-black/80"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      >
        <path d="M70 20 L40 40 L20 90 L48 100 L48 220 L172 220 L172 100 L200 90 L180 40 L150 20 L130 40 C130 60 90 60 90 40 Z" />
        <path d="M90 40 C100 50 120 50 130 40" />
        <path d="M48 100 C 50 110 60 115 70 110" />
        <path d="M172 100 C 170 110 160 115 150 110" />
      </svg>
      <div
        aria-hidden
        className="absolute left-[10%] right-[10%] top-[20%] border-t border-dashed border-black/30"
      />
      <div
        aria-hidden
        className="absolute right-[2%] top-[12%] bottom-[58%] border-l border-dashed border-black/30"
      />
      <div
        aria-hidden
        className="absolute left-[14%] right-[14%] top-[42%] border-t border-dashed border-black/25"
      />
      <div
        aria-hidden
        className="absolute left-[2%] top-[12%] bottom-[6%] border-l border-dashed border-black/30"
      />
    </div>
  );
}

function TrousersVisual() {
  return (
    <div className="relative w-44 h-56">
      <svg
        viewBox="0 0 200 260"
        className="absolute inset-0 w-full h-full text-black/80"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      >
        <path d="M40 10 L160 10 L165 60 L150 250 L115 250 L102 90 L98 90 L85 250 L50 250 L35 60 Z" />
        <path d="M40 22 L160 22" />
        <path d="M85 250 C 92 220 100 110 100 90" />
        <path d="M115 250 C 108 220 100 110 100 90" />
      </svg>
      <div
        aria-hidden
        className="absolute left-[18%] right-[18%] top-[8%] border-t border-dashed border-black/30"
      />
      <div
        aria-hidden
        className="absolute left-1/2 top-[35%] bottom-[3%] border-l border-dashed border-black/30"
      />
      <div
        aria-hidden
        className="absolute left-[58%] right-[24%] bottom-[2%] border-t border-dashed border-black/30"
      />
    </div>
  );
}
